// A sessão da equipe da Resulti — a única do portal (branch web: só a Área Resulti). Context do React,
// sem biblioteca de estado: é um objeto pequeno que muda raramente. O que muda muito — os dados das
// telas — fica no TanStack Query, com a chave começando por 'admin', limpa ao entrar, sair ou perder
// a sessão.
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eu } from '../api/admin';
import { http } from '../api/client';
import type { NivelSuporte, UsuarioAdmin } from '../api/tipos';
import { pode, podeConfigurarSuporte, type Capacidade } from '../lib/papeis';

/** A raiz de toda chave de cache do portal. */
export const CACHE = 'admin';

/** O `GET /auth/eu` (o Início lê a mesma chave): quem está logado, com o nível no suporte. */
export const CHAVE_EU = [CACHE, 'eu'] as const;

/**
 * verificando → renovando pelo cookie de refresh (o carregamento da página).
 * ativa       → há usuário e token em memória.
 * ausente     → não há sessão: a tela protegida manda para o login.
 */
export type SituacaoSessao = 'verificando' | 'ativa' | 'ausente';

export interface Sessao {
  usuario: UsuarioAdmin | null;
  situacao: SituacaoSessao;
  /**
   * O nível no suporte (LEITURA = só ver; CONFIGURAR = também entrar na loja). O login e o renovar não o
   * trazem: vem do `GET /auth/eu`, lido ao entrar e relido a cada renovação (o ADMIN pode ter mudado o
   * nível). O ADMIN é sempre CONFIGURAR. null = ainda não chegou.
   */
  suporteNivel: NivelSuporte | null;
  /**
   * A pessoa clicou em "Sair" (e não a sessão venceu). Muda só o redirecionamento: depois de sair vai
   * para o login limpo; depois de vencer, o login guarda o `?volta=` para retomar a tela.
   */
  saiu: boolean;
  /** O aviso que o login mostra depois de uma saída pedida pelo próprio portal ("Senha trocada…"). */
  aviso: string | null;
  entrar: (usuario: UsuarioAdmin, acesso: string) => void;
  sair: () => Promise<void>;
  /**
   * Acaba a sessão SÓ aqui, sem chamar `/auth/sair`: o servidor já a derrubou (a troca da própria senha
   * encerra todas as sessões e apaga o cookie). O login abre limpo, com o `aviso`.
   */
  encerrar: (aviso: string) => void;
}

const Ctx = createContext<Sessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }): JSX.Element {
  const qc = useQueryClient();
  const [estado, setEstado] = useState<{ situacao: SituacaoSessao; usuario: UsuarioAdmin | null; saiu?: boolean; aviso?: string }>({
    situacao: 'verificando',
    usuario: null,
  });

  // A restauração roda UMA vez por carregamento da página. A promessa fica num `ref` (e não um
  // booleano): sob o StrictMode o efeito monta, desmonta e monta de novo, e duas renovações seguidas
  // seriam fatais — o refresh é ROTACIONADO a cada uso e a 2ª apresentaria um refresh já trocado, que a
  // API trata como roubo e responde derrubando todas as sessões. (A promessa de renovação do cliente
  // HTTP também é compartilhada; aqui é a segunda trava.)
  const restauracao = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (restauracao.current) return;
    restauracao.current = http.renovar().then((d) => {
      setEstado((e) => {
        // Entrou pelo login enquanto restaurava: o login vale.
        if (e.situacao === 'ativa') return e;
        return d ? { situacao: 'ativa', usuario: d.usuario } : { situacao: 'ausente', usuario: null };
      });
    });
  }, []);

  useEffect(
    () =>
      http.assinarPerdaDeSessao(() => {
        qc.removeQueries({ queryKey: [CACHE] });
        setEstado({ situacao: 'ausente', usuario: null });
      }),
    [qc],
  );

  // A cada renovação o servidor devolve o usuário de novo: nome e papel mudados por outra pessoa
  // aparecem sem precisar sair e entrar. O nível no suporte não vem junto: relê o `/auth/eu`.
  useEffect(
    () =>
      http.assinarRenovacao((u) => {
        setEstado((e) => (e.situacao === 'ativa' ? { ...e, usuario: u } : e));
        void qc.invalidateQueries({ queryKey: CHAVE_EU });
      }),
    [qc],
  );

  // O nível no suporte, do `GET /auth/eu`. A chave fica sob 'admin': entrar, sair ou perder a sessão
  // limpam o cache, e a próxima pessoa lê o dela.
  const daEu = useQuery({ queryKey: CHAVE_EU, queryFn: eu, enabled: estado.situacao === 'ativa', staleTime: 60_000 });

  const entrar = useCallback(
    (usuario: UsuarioAdmin, acesso: string) => {
      http.definirToken(acesso);
      // Outra pessoa pode ter usado este navegador antes: nada do cache dela aparece.
      qc.removeQueries({ queryKey: [CACHE] });
      setEstado({ situacao: 'ativa', usuario });
    },
    [qc],
  );

  const sair = useCallback(async () => {
    await http.sair();
    qc.removeQueries({ queryKey: [CACHE] });
    setEstado({ situacao: 'ausente', usuario: null, saiu: true });
  }, [qc]);

  const encerrar = useCallback(
    (aviso: string) => {
      http.definirToken(null);
      qc.removeQueries({ queryKey: [CACHE] });
      setEstado({ situacao: 'ausente', usuario: null, saiu: true, aviso });
    },
    [qc],
  );

  const usuarioEu = daEu.data;
  const suporteNivel: NivelSuporte | null =
    estado.usuario?.papel === 'ADMIN'
      ? 'CONFIGURAR'
      : usuarioEu && estado.usuario && usuarioEu.id === estado.usuario.id
        ? usuarioEu.suporteNivel
        : null;

  const valor = useMemo<Sessao>(
    () => ({
      usuario: estado.usuario,
      situacao: estado.situacao,
      suporteNivel,
      saiu: Boolean(estado.saiu),
      aviso: estado.aviso ?? null,
      entrar,
      sair,
      encerrar,
    }),
    [estado, suporteNivel, entrar, sair, encerrar],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSessao(): Sessao {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSessao fora do ProvedorSessao');
  return c;
}

/** O que o PAPEL de quem está logado permite MOSTRAR (lib/papeis.ts). A guarda real é do servidor. */
export function usePode(): (capacidade: Capacidade) => boolean {
  const { usuario } = useSessao();
  return useCallback((c: Capacidade) => pode(usuario?.papel, c), [usuario]);
}

/**
 * Quem está logado pode CONFIGURAR no suporte (o acesso CONFIGURAR e a entrada na loja)? Depende do nível,
 * não do papel (lib/papeis.ts, `podeConfigurarSuporte`). false enquanto o nível não chegou.
 */
export function usePodeConfigurarSuporte(): boolean {
  const { usuario, suporteNivel } = useSessao();
  return podeConfigurarSuporte(usuario?.papel, suporteNivel);
}
